
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CodeEditor } from './CodeEditor';

export function WebhookTriggers() {
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Form state
  const [eventTypeId, setEventTypeId] = useState('');
  const [endpointId, setEndpointId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [customVariables, setCustomVariables] = useState('{}');
  const [isActive, setIsActive] = useState(true);

  // Query triggers
  const { data: triggers, isLoading } = useQuery({
    queryKey: ['webhook-triggers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_triggers')
        .select(`
          *,
          webhook_event_types (event_code),
          webhook_endpoints (url, description),
          webhook_payload_templates (template_name)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  // Query available event types
  const { data: eventTypes } = useQuery({
    queryKey: ['webhook-event-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_event_types')
        .select('*')
        .eq('is_active', true)
        .order('event_code', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  // Query available endpoints
  const { data: endpoints } = useQuery({
    queryKey: ['webhook-endpoints'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_endpoints')
        .select('*')
        .eq('is_active', true)
        .order('url', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  // Query available templates
  const { data: templates } = useQuery({
    queryKey: ['webhook-payload-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_payload_templates')
        .select('*')
        .order('template_name', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  // Create or update trigger
  const mutation = useMutation({
    mutationFn: async (values: any) => {
      let parsedVariables;
      try {
        parsedVariables = values.customVariables ? JSON.parse(values.customVariables) : null;
      } catch (error) {
        throw new Error('Invalid JSON in custom variables');
      }
      
      if (editItem) {
        // Update
        const { data, error } = await supabase
          .from('webhook_triggers')
          .update({
            event_type_id: values.eventTypeId,
            endpoint_id: values.endpointId,
            payload_template_id: values.templateId === 'none' ? null : values.templateId,
            custom_variables: parsedVariables,
            is_active: values.isActive,
          })
          .eq('trigger_id', editItem.trigger_id)
          .select();
        
        if (error) throw error;
        return data;
      } else {
        // Create
        const { data, error } = await supabase
          .from('webhook_triggers')
          .insert({
            event_type_id: values.eventTypeId,
            endpoint_id: values.endpointId,
            payload_template_id: values.templateId === 'none' ? null : values.templateId,
            custom_variables: parsedVariables,
            is_active: values.isActive,
          })
          .select();
        
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-triggers'] });
      toast({
        title: `Trigger ${editItem ? 'Updated' : 'Created'}`,
        description: `Successfully ${editItem ? 'updated' : 'created'} the webhook trigger.`,
      });
      resetForm();
      setOpen(false);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Reset form
  const resetForm = () => {
    setEventTypeId('');
    setEndpointId('');
    setTemplateId('');
    setCustomVariables('{}');
    setIsActive(true);
    setEditItem(null);
  };

  // Open dialog for editing
  const handleEdit = (item: any) => {
    setEditItem(item);
    setEventTypeId(item.event_type_id.toString());
    setEndpointId(item.endpoint_id.toString());
    setTemplateId(item.payload_template_id ? item.payload_template_id.toString() : 'none');
    setCustomVariables(item.custom_variables ? JSON.stringify(item.custom_variables, null, 2) : '{}');
    setIsActive(item.is_active);
    setOpen(true);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    mutation.mutate({
      eventTypeId: parseInt(eventTypeId),
      endpointId: parseInt(endpointId),
      templateId: templateId === 'none' ? null : parseInt(templateId),
      customVariables,
      isActive,
    });
  };

  // Test trigger mutation
  const testMutation = useMutation({
    mutationFn: async (triggerId: number) => {
      // Find the event type for this trigger
      const trigger = triggers?.find(t => t.trigger_id === triggerId);
      if (!trigger) throw new Error('Trigger not found');

      // Call the edge function to trigger the webhook with test data
      const { error } = await supabase.functions.invoke('trigger-webhook', {
        body: {
          event_code: trigger.webhook_event_types.event_code,
          event_data: {
            test: true,
            timestamp: new Date().toISOString(),
            message: 'This is a test event triggered manually'
          }
        }
      });
      
      if (error) throw error;
      return triggerId;
    },
    onSuccess: () => {
      toast({
        title: 'Test Webhook Triggered',
        description: 'The test webhook has been queued for delivery.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to trigger test webhook',
        variant: 'destructive',
      });
    },
  });

  // Get available templates for selected event type
  const getFilteredTemplates = () => {
    if (!templates || !eventTypeId) return [];
    return templates.filter(t => t.event_type_id.toString() === eventTypeId);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Webhook Triggers</CardTitle>
        <Button onClick={() => { resetForm(); setOpen(true); }}>
          Add Trigger
        </Button>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <ScrollArea className="h-[60vh] w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event Type</TableHead>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {triggers?.map((item) => (
                  <TableRow key={item.trigger_id}>
                    <TableCell className="font-medium">{item.webhook_event_types.event_code}</TableCell>
                    <TableCell className="max-w-xs truncate">{item.webhook_endpoints.url}</TableCell>
                    <TableCell>{item.webhook_payload_templates?.template_name || 'None'}</TableCell>
                    <TableCell>
                      {item.is_active ? (
                        <span className="text-green-500">Active</span>
                      ) : (
                        <span className="text-gray-500">Inactive</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => testMutation.mutate(item.trigger_id)}
                          disabled={testMutation.isPending}
                        >
                          Test
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit' : 'Add'} Webhook Trigger</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="event-type">Event Type</Label>
                <Select 
                  value={eventTypeId} 
                  onValueChange={(value) => {
                    setEventTypeId(value);
                    setTemplateId('none'); // Reset template when event type changes
                  }}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Event Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {eventTypes?.map((type) => (
                      <SelectItem key={type.event_type_id} value={type.event_type_id.toString()}>
                        {type.event_code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="endpoint">Endpoint</Label>
                <Select 
                  value={endpointId} 
                  onValueChange={setEndpointId}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Endpoint" />
                  </SelectTrigger>
                  <SelectContent>
                    {endpoints?.map((endpoint) => (
                      <SelectItem key={endpoint.endpoint_id} value={endpoint.endpoint_id.toString()}>
                        {endpoint.description || endpoint.url}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="template">Payload Template (Optional)</Label>
                <Select 
                  value={templateId} 
                  onValueChange={setTemplateId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Template (Optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Template</SelectItem>
                    {getFilteredTemplates().map((template) => (
                      <SelectItem key={template.template_id} value={template.template_id.toString()}>
                        {template.template_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Optional: Select a payload template for this event type.
                </p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="custom-variables">Custom Variables (Optional, JSON)</Label>
                <CodeEditor
                  value={customVariables}
                  onChange={setCustomVariables}
                  language="json"
                  height="200px"
                />
                <p className="text-sm text-muted-foreground">
                  Optional: Add custom variables that will be included in every webhook payload.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Switch 
                  id="is-active" 
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
                <Label htmlFor="is-active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
